import { TextButton, Toast } from '@modusign/design-system';
import { createLabel } from '@modusign/openapi-client-category';
import { transferDocument } from '@modusign/openapi-client-external-document';
import { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import { PromiseType } from 'utility-types';

import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import TransferResourceModal from '@app/features/workspaceManagement/components/TransferResourceModal';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import pushAdobeDataLayer from '@app/libs/analytics/adobe';
import delay from '@app/libs/delay';
import workspaceService from '@app/libs/workspaceService';

interface ExternalDocumenttransferModalProps {
  documents: Array<{ id: string; title: string }>;
  onCancel: () => void;
  onComplete: () => void;
}

export default function ExternalDocumenttransferModal({
  documents,
  onCancel,
  onComplete,
}: ExternalDocumenttransferModalProps) {
  const workspaceId = useWorkspaceId();
  const { handleError } = useOpenErrorModal();

  const transfer = useMutation<
    PromiseType<ReturnType<typeof transferDocument>>,
    AxiosError,
    {
      targetWorkspaceId: string;
      targetWorkspaceMemberId: string;
      targetWorkspaceName: string;
      labelName: string;
      documentIds: string[];
    }
  >(
    async (params) => {
      const label = await createLabel(params.targetWorkspaceId, {
        name: params.labelName,
        roles: [
          { name: 'EDITOR', subjectId: params.targetWorkspaceMemberId, subjectType: 'MEMBER' },
        ],
      });

      // 라벨 만든 후 권한 동기화가 느려서 딜레이를 줍니다.
      // see. https://modusign.atlassian.net/browse/QA-1023?focusedCommentId=13738
      await delay(1000);

      return transferDocument(workspaceId, {
        targetWorkspaceId: params.targetWorkspaceId,
        transferDocuments: params.documentIds.map((id) => ({
          originDocumentId: id,
          targetLabelIds: [label.id],
        })),
      });
    },
    {
      onError: (error) => {
        handleError({ title: '문서 이전 실패', error });
        // 오류 모달이 이 모달에 가려져서 실패해도 모달을 닫도록 합니다.
        onCancel();
      },
      onSuccess: (data, params) => {
        onComplete();
        const succeeded = data.results.length - data.error;
        pushAdobeDataLayer({
          event: 'transferExternalDocuments',
          transfer: {
            requestCount: params.documentIds.length,
            successCount: succeeded,
          },
        });
        if (succeeded === 0) {
          Toast.warning({
            description: '이전할 수 있는 계약이 없습니다.',
          });
        } else {
          Toast.success({
            description: (
              <>
                {data.error > 0 ? '이전할 수 없는 계약을 제외한 ' : ''}
                {succeeded}개의 계약을 이전했습니다.
                <br />
                <TextButton
                  onClick={() => workspaceService.setWorkspaceId(params.targetWorkspaceId)}
                >
                  {`'${params.targetWorkspaceName}'에서 확인`}
                </TextButton>
              </>
            ),
          });
        }
      },
    }
  );

  if (transfer.isLoading) {
    return <GlobalLoadingSpinner />;
  }

  return (
    <TransferResourceModal
      needLabel
      resourceNames={documents.map((d) => d.title)}
      resourceType="EXTERNAL_DOCUMENT"
      onCancel={onCancel}
      onConfirm={(params) =>
        transfer.mutate({
          ...params,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          labelName: params.labelName!,
          documentIds: documents.map((d) => d.id),
        })
      }
    />
  );
}
